import React, {useState} from "react";
import {Container, Nav, Navbar} from "react-bootstrap";
import {Link, NavLink, useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import {useCurrentUser} from "Models/User/useCurrentUser";
import {UserPermissions} from "Models/User/UserUtils";
import "./TopMenu.styles.jsx";
import GSPSProfileAvatar from "./GSPSProfileAvatar";
import {TopMenuContainer} from "./TopMenu.styles.jsx";
import GSPSAccountNavActions from "./GSPSAccountNavActions.jsx";
import LogoutIcon from "@mui/icons-material/Logout";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link as MuiLink,
    ListItemIcon,
    MenuItem,
    Slide,
    Tooltip,
    Typography,
    useScrollTrigger,
} from "@mui/material";
import {SettingsSharp} from "@mui/icons-material";
import {SubscriberTierID} from "../../Models/Subscriber/SubscriberConstants";
import {BiSolidUserAccount} from "react-icons/bi";
import {RiBuilding4Line} from "react-icons/ri";

const HideOnScroll = (props) => {
    const {children, window} = props;

    const trigger = useScrollTrigger({
        target: window ? window() : undefined,
    });

    return (
        <Slide appear={false} direction="down" in={!trigger}>
            {children}
        </Slide>
    );
};

export const TopMenu = ({resetCurrentUserSession}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
    const openAccountMenu = Boolean(anchorEl);
    const navigate = useNavigate();
    const handleClose = () => {
        setAnchorEl(null);
    };
    const currentUser = useCurrentUser();
    const isAdmin = UserPermissions.useIsAdmin();
    const isSuperAdmin = UserPermissions.useIsSuperAdmin();

    const handleUserLogout = () => {
        resetCurrentUserSession(navigate);
    };

    const handleOpenAccountMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const checkConditions = (conditions, isAdmin, currentUser, isSuperAdmin) => {
        if (!conditions || conditions.length === 0) return true;
        return conditions.every((condition) => {
            switch (condition) {
                case "isAdmin":
                    return isAdmin;
                case "isSuperAdmin":
                    return isSuperAdmin;
                case "isNotAdmin":
                    return !isAdmin;
                case "isUpgrade":
                    return Number(currentUser.subscriber_tier_id) !== SubscriberTierID.ELITE;
                default:
                    return true;
            }
        });
    };

    function getAccountActions() {
        const accountActions = [];
        if (isSuperAdmin) {
            accountActions.push(<MenuItem key={4} onClick={() => navigate("/subscribers")} className="py-1">
                <ListItemIcon>
                    <RiBuilding4Line fontSize="small" />
                </ListItemIcon>
                Subscribers
            </MenuItem>);
        }

        if (isAdmin) {
            accountActions.push(
                <MenuItem key={3} onClick={() => navigate("/users")} className="py-1">
                    <ListItemIcon>
                        <BiSolidUserAccount fontSize="small" />
                    </ListItemIcon>
                    Users
                </MenuItem>,
                <MenuItem key={2} onClick={() => navigate("/subscriber_settings")} className="py-1">
                    <ListItemIcon>
                        <SettingsSharp fontSize="small" />
                    </ListItemIcon>
                    Settings
                </MenuItem>,
            );
        }

        accountActions.push(
            <MenuItem key={1} onClick={handleUserLogout} className="py-1">
                <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Sign out
            </MenuItem>,
        );

        return accountActions;
    }

    const navContent = {
        navLinks: [
            {
                "title": "Dashboard",
                "url": "/dashboard",
                "conditions": [],
            },
            {
                "title": "Locations",
                "url": "/locations",
                "conditions": ["isAdmin"],
            },
            {
                "title": "Accounts",
                "url": "/accounts",
                "conditions": ["isAdmin"],
            },
            {
                "title": "Reports",
                "url": "/reports",
                "conditions": ["isAdmin"],
            },
            {
                "title": "Media",
                "url": "/media_reports",
                "conditions": ["isAdmin"],
            },
            {
                "title": "Upgrade",
                "url": "https://www.sitevisionpro.com/pricing/",
                "conditions": ["isUpgrade"],
            },
            {
                "title": "Support",
                "type": "modal",
                "modalKey": "supportModal",
                "conditions": [],
            },
        ],
        accountActions: getAccountActions(),
    };

    const displayNavLinks = () => {
        return (
            <>
                {navContent.navLinks.map((navLink, index) => {
                    if (!checkConditions(navLink.conditions, isAdmin, currentUser, isSuperAdmin)) {
                        return null;
                    }

                    if (navLink.type === "modal" && navLink.modalKey === "supportModal") {
                        return (
                            <button
                                key={index}
                                className="navLink mx-md-3 mb-4 mb-lg-0 pb-1 menu-link-md btn btn-link p-0"
                                onClick={() => setIsSupportModalOpen(true)}
                            >
                                {navLink.title}
                            </button>
                        );
                    }

                    if (navLink.title === "Upgrade") {
                        return (
                            <a
                                key={index}
                                className="navLink mx-md-3 mb-4 mb-lg-0 pb-1 menu-link-md"
                                href={navLink.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {navLink.title}
                            </a>
                        );
                    }

                    return (
                        <NavLink
                            key={index}
                            className="navLink mx-md-3 mb-4 mb-lg-0 pb-1 menu-link-md"
                            to={navLink.url}
                        >
                            {navLink.title}
                        </NavLink>
                    );
                })}
            </>
        );
    };

    function renderSupportDialog() {
        return <Dialog
            open={isSupportModalOpen}
            onClose={() => setIsSupportModalOpen(false)}
        >
            <DialogTitle>Support</DialogTitle>
            <DialogContent dividers={true}
                sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography gutterBottom={true}>
                    For more information, watch our How To's videos{" "}
                    <MuiLink
                        href="https://www.youtube.com/playlist?list=PL8QmZnJWRD59mL7Gf2NKK4rGHCv6xIrj-"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        here
                    </MuiLink>
                </Typography>
                <Typography sx={{mt: 3}}>
                    For further support, {" "}
                    <MuiLink href="mailto:support@sitevisionpro.com">
                        Contact Us
                    </MuiLink>
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setIsSupportModalOpen(false)}>Close</Button>
            </DialogActions>
        </Dialog>;
    }

    return (
        <HideOnScroll>
            <TopMenuContainer>
                {renderSupportDialog()}

                <Navbar collapseOnSelect={true} expand="lg" className="TopMenu navbar-expand-lg py-2 z-3"
                    data-bs-theme="light">
                    <Container>
                        <Navbar.Brand>
                            <Link className="logo my-2" to={"/dashboard"}>
                                <img src="/SVP_dark_bg.png" className="img-fluid" alt="logo" />
                            </Link>
                        </Navbar.Brand>
                        <Navbar.Toggle className="menuToggler p-0" aria-controls="responsive-navbar-nav">
                            <span></span>
                            <span></span>
                            <span></span>
                        </Navbar.Toggle>
                        <Navbar.Collapse id="responsive-navbar-nav" className="justify-content-end">
                            <Nav className="justify-content-md-center align-items-md-center">
                                {displayNavLinks()}
                            </Nav>
                            <div className="accountMenu position-relative">
                                <Tooltip title="Account settings">
                                    <IconButton
                                        onClick={handleOpenAccountMenu}
                                        aria-controls={openAccountMenu ? "account-menu" : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={openAccountMenu ? "true" : undefined}
                                        className="p-0"
                                    >
                                        <GSPSProfileAvatar width={40} height={40} />
                                    </IconButton>
                                </Tooltip>
                                <div className="mb-4 mb-lg-0">
                                    <GSPSAccountNavActions
                                        open={openAccountMenu}
                                        handleClose={handleClose}
                                        anchorEl={anchorEl}
                                        user={currentUser}
                                        actions={navContent.accountActions}
                                    />
                                </div>
                            </div>
                        </Navbar.Collapse>
                    </Container>
                </Navbar>
            </TopMenuContainer>
        </HideOnScroll>
    );
};

TopMenu.propTypes = {
    resetCurrentUserSession: PropTypes.func.isRequired,
};

HideOnScroll.propTypes = {
    children: PropTypes.node.isRequired,
    window: PropTypes.func,
};

